import React, { createContext, useState, useEffect, useCallback, useRef } from 'react';
import authEvents from '../utils/authEvents';
import authSyncService from '../services/authSyncService';
import tokenValidator from '../utils/tokenValidator';
import publicApiService from '../services/api/publicApi';
import { renewSession, isSessionRejection } from '../utils/sessionRenewal';

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const refreshTimerRef = useRef(null);

  // No longer need local token validation - using tokenValidator utility

  // Ends the session: storage cleanup is delegated to the canonical helper so the key
  // list lives in exactly one place, and any in-flight refresh is invalidated.
  const clearAuthData = useCallback(() => {
    tokenValidator.endSession();

    // Update state
    setUser(null);
    setIsAuthenticated(false);
  }, []);

  useEffect(() => {
    let cancelled = false;

    const restoreSession = async () => {
      // Use tokenValidator to get valid token (auto-cleans invalid ones)
      let token = tokenValidator.getValidToken();
      const refreshToken = localStorage.getItem('refreshToken');

      // The 1-hour access token has expired but the long-lived refresh token is still
      // here: renew silently so the user keeps their session instead of being bounced
      // to the login screen. This is what makes "تذكرني" observable.
      if (!token && refreshToken && !tokenValidator.isSessionRemembered()) {
        // «تذكرني» was unticked and the app is being opened again after the pass ran out: ask for the
        // password, and end this sign-in on the server too (fire-and-forget, Local-First).
        publicApiService.logout(null, refreshToken).catch(() => {});
        tokenValidator.cleanupAuthStorage();
        tokenValidator.markSessionExpired();
      } else if (!token && refreshToken) {
        try {
          token = await renewSession(null);
        } catch (error) {
          // Only a server refusal (expired/ended session, locked or inactive account) means the
          // session is really over. A network failure or server fault must not cost the user
          // their session — leave the refresh token in place and retry next load.
          if (isSessionRejection(error)) {
            tokenValidator.cleanupAuthStorage();
            tokenValidator.markSessionExpired();
          }
          console.error('❌ AuthContext: session restore failed');
        }
      }

      if (cancelled) return;

      if (token) {
        setIsAuthenticated(true);

        const userId = localStorage.getItem('userId');

        if (userId) {
          const restoredUser = {
            userId,
            role: localStorage.getItem('userRole') || 'turnKeyOwner',
            name: localStorage.getItem('userName') || '',
            locale: localStorage.getItem('userLocale') || 'ar'
          };
          setUser(restoredUser);
        } else {
          // Try to extract user info from token
          const payload = tokenValidator.decodeJWT(token);
          if (payload && payload.userId) {
            setUser({ userId: payload.userId });
            localStorage.setItem('userId', payload.userId);
          }
        }
      }

      setLoading(false);
    };

    restoreSession();

    return () => {
      cancelled = true;
    };
  }, []);

  // Listen to auth events - Industry standard event-driven approach
  useEffect(() => {
    const handleLogoutRequired = () => {
      clearAuthData();
      // Navigation will be handled by AppSecure when isAuthenticated changes
    };

    const handleTokenExpired = () => {
      clearAuthData();
      // Navigation will be handled by AppSecure when isAuthenticated changes
    };

    // Subscribe to events
    const unsubscribeLogout = authEvents.onLogoutRequired(handleLogoutRequired);
    const unsubscribeExpiry = authEvents.onTokenExpired(handleTokenExpired);

    return () => {
      unsubscribeLogout();
      unsubscribeExpiry();
    };
  }, [clearAuthData]);

  // v3.0: Refresh access token using refresh token
  // Renewal goes through the shared, tab-safe renewSession(). Signs out ONLY when the server refuses
  // the session — a network error (e.g. a laptop waking before Wi-Fi is back) keeps the user signed in
  // and the next tick retries.
  const refreshAccessToken = useCallback(async () => {
    try {
      const accessToken = await renewSession();
      authSyncService.broadcastTokenRefresh(accessToken, 3600);
      return true;
    } catch (error) {
      console.error('❌ AuthContext: Token refresh failed');
      if (isSessionRejection(error)) {
        authEvents.emitLogoutRequired();
      }
      return false;
    }
  }, []);

  // v3.0: Auto-refresh timer - Check token expiry and refresh before expiration
  useEffect(() => {
    if (!isAuthenticated) {
      // Clear timer if user logged out
      if (refreshTimerRef.current) {
        clearInterval(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }
      return;
    }

    let isRefreshInProgress = false; // Prevent concurrent refresh attempts

    const checkAndRefreshToken = async () => {
      // Prevent concurrent refresh attempts
      if (isRefreshInProgress) {
        return;
      }

      const token = localStorage.getItem('token');
      const refreshToken = localStorage.getItem('refreshToken');

      if (!token || !refreshToken) {
        authEvents.emitLogoutRequired();
        return;
      }

      // Validate token structure
      if (!tokenValidator.isValidJWT(token)) {
        tokenValidator.cleanupAuthStorage();
        authEvents.emitLogoutRequired();
        return;
      }

      // Check if token is expired
      if (tokenValidator.isTokenExpired(token)) {
        isRefreshInProgress = true;
        const success = await refreshAccessToken();
        isRefreshInProgress = false;

        if (!success) {
          // Error already logged in refreshAccessToken
        }
        return;
      }

      // Check if token expires in < 5 minutes
      const payload = tokenValidator.decodeJWT(token);
      if (payload && payload.exp) {
        const expiryTime = payload.exp * 1000;
        const now = Date.now();
        const timeUntilExpiry = expiryTime - now;
        const fiveMinutes = 5 * 60 * 1000;

        if (timeUntilExpiry < fiveMinutes && timeUntilExpiry > 0) {
          isRefreshInProgress = true;
          await refreshAccessToken();
          isRefreshInProgress = false;
        }
      }
    };

    // Delay initial check slightly to avoid race condition on app init
    const initialCheckTimer = setTimeout(() => {
      checkAndRefreshToken();
    }, 100);

    // Check every minute
    refreshTimerRef.current = setInterval(checkAndRefreshToken, 60 * 1000);

    // Check on visibility change (when tab becomes active)
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        checkAndRefreshToken();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearTimeout(initialCheckTimer);
      if (refreshTimerRef.current) {
        clearInterval(refreshTimerRef.current);
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isAuthenticated, refreshAccessToken]);

  const login = (token, userData, refreshToken = null) => {
    // Defense in depth: clear stale account selection from any previous session
    // Prevents non-staff users from inheriting a staff user's account filter
    sessionStorage.removeItem('hostme_selected_account');

    // Open a new session so token refreshes are accepted again
    tokenValidator.beginSession();

    // Validate token before storing
    if (!tokenValidator.isValidJWT(token)) {
      console.error('❌ AuthContext: Invalid token format, cannot login');
      return;
    }

    try {
      // Single writer for the token pair (validates, and honours session state)
      const stored = tokenValidator.storeTokenPair({ accessToken: token, refreshToken });
      if (!stored) {
        console.error('❌ AuthContext: Failed to store token');
        return;
      }

      if (userData) {
        localStorage.setItem('userId', userData.userId);
        localStorage.setItem('userRole', userData.role);
        localStorage.setItem('userName', userData.name || '');
        localStorage.setItem('userLocale', userData.locale || 'ar');

        const newUser = {
          userId: userData.userId,
          role: userData.role,
          name: userData.name,
          email: userData.email,
          locale: userData.locale || 'ar'
        };
        setUser(newUser);

        // v3.0: Broadcast login to other tabs
        authSyncService.broadcastLogin(newUser, token);
      }
    } catch (error) {
      console.error('❌ AuthContext: Error saving to localStorage:', error);
    }

    setIsAuthenticated(true);
  };

  // v3.0: Update token (for refresh). `broadcast: false` when applying another tab's message — re-sending
  // it made two open tabs bounce the same message back and forth indefinitely.
  const updateToken = useCallback((newToken, expiresIn, { broadcast = true } = {}) => {
    try {
      tokenValidator.storeTokenPair({ accessToken: newToken });

      if (broadcast) {
        authSyncService.broadcastTokenRefresh(newToken, expiresIn);
      }
    } catch (error) {
      console.error('❌ AuthContext: Error updating token:', error);
    }
  }, []);

  // The single logout path for the whole application. Nothing else may clear auth
  // storage or navigate — see docs/03_Technical/AUTHENTICATION_ARCHITECTURE.md.
  //
  // `options.broadcast: false` when applying another tab's LOGOUT message, so it is not echoed back.
  // (Also safe as an onClick handler: a click event carries no `broadcast` field.)
  const logout = useCallback((options) => {
    const broadcast = options?.broadcast !== false;

    // Clear refresh timer
    if (refreshTimerRef.current) {
      clearInterval(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }

    // Capture both before clearing. The renewable token identifies THIS device's session even when the
    // 1-hour pass has just run out, so the server can always end it.
    const token = localStorage.getItem('token');
    const refreshToken = localStorage.getItem('refreshToken');

    // Local state first: the user asked to log out, so it must never depend on the
    // network. Also marks the session ended, so any in-flight refresh is discarded.
    clearAuthData();

    if (broadcast) {
      authSyncService.broadcastLogout();
    }

    // End this device's session server-side (other devices stay signed in). Fire-and-forget per the
    // Local-First pattern — a failure here (offline, server down) must not block the logout.
    if (token || refreshToken) {
      publicApiService.logout(token, refreshToken).catch(() => {
        console.warn('⚠️ Server-side logout failed; local session already ended');
      });
    }

    // Navigation handled by AppSecure when isAuthenticated becomes false
  }, [clearAuthData]);

  return (
    <AuthContext.Provider value={{ user, isAuthenticated, loading, login, logout, updateToken }}>
      {children}
    </AuthContext.Provider>
  );
};
